import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Survey, Model } from '@navriti/forms';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from './Modal'; // Import the Modal component
import { API_URLS, MESSAGES } from '../messages.js/viewerTexts'; // Import constants and messages from config.js
import { useNavigate } from 'react-router-dom';
import '../../pages/viewer.css';
import { buildSurveyResponseData } from '../../utils/buildSurveyResponseData';
import { bindSurveyFileHandlers, applyFileSourceTypes } from '../../utils/surveyFileHandlers';
import { ensureS3FileQuestions } from '../../utils/ensureS3FileQuestions';
import { sanitizeSurveyFormJson } from '../../utils/sanitizeSurveyJson';

export default function App() {
  const [formArray, setFormArray] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [showModal, setShowModal] = useState(false); // State for modal visibility
  const [formId, setFormId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const getTokenFromCookie = () => {
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('token='));

    if (tokenCookie) {
      return tokenCookie.split('=')[1];
    } else {
      return null;
    }
  };

  // Function to fetch decrypted formId from API
  const fetchDecryptedId = async (encryptedId) => {
    try {
      const token = getTokenFromCookie();
      const response = await fetch(API_URLS.DECRYPT_ID, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          encryptedId: encryptedId,
        }),
      });

      if (!response.ok) {
        throw new Error(MESSAGES.DECRYPT_ID_FAIL);
      }

      const decryptedData = await response.json();
     
      return decryptedData.decryptedId;
    } catch (error) {
      console.error('Error decrypting ID:', error);
      throw error;
    }
  };

  const checkFormValidity = async (data) => {
    const currentDate = new Date();
    const startDate = new Date(data.dateRange[0]);
    const endDate = new Date(data.dateRange[1]);
    
    return currentDate >= startDate && currentDate <= endDate;
  };

  const checkForm = async (formId) => {
    try {
      const token = getTokenFromCookie();
      const response = await fetch(`${API_URLS.ARRAY_FORM}?id=${formId}`, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(MESSAGES.FETCH_FORM_FAIL);
      }

      const formData = await response.json();
     
      const isValid = await checkFormValidity(formData);
      
      if (!isValid && formData.type !== 'open') {
        navigate("/NoResponsePage");
      }

      setFormArray(formData.formData);
      setFormId(formData.id);
    } catch (error) {
      console.error('Error fetching form data:', error);
    }
  };

  const alertResults = useCallback(async (sender) => {
    console.log('sender', sender);

    try {
      const searchParams = new URLSearchParams(location.search);
      const encryptedId = searchParams.get('id');
      const decryptedId = await fetchDecryptedId(encryptedId);

      const token = getTokenFromCookie();
      const responseData = buildSurveyResponseData(sender);

      const response = await fetch(API_URLS.RESPONSE_SAVE, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formId: decryptedId,
          data: responseData,
        }),
      });

      if (!response.ok) {
        throw new Error(MESSAGES.SAVE_RESPONSE_FAIL);
      }

      toast.success(MESSAGES.SAVE_RESPONSE_SUCCESS);
    } catch (error) {
      console.error('Error saving response data:', error);
      toast.error(MESSAGES.SAVE_RESPONSE_FAIL);
    }
  }, [location.search, getTokenFromCookie]);

  useEffect(() => {
  if (!formArray?.logo) return;
  const interval = setInterval(() => {
    const formEl = document.querySelector('form[class*="_form_"]');
    if (formEl) {
      if (formEl.querySelector('.form-logo-wrapper')) {
        clearInterval(interval);
        return;
      }
      const logoWrapper = document.createElement('div');
      logoWrapper.className = 'form-logo-wrapper';
      logoWrapper.style.cssText = 'text-align:left;padding:0;margin-bottom:-16px;';
      const img = document.createElement('img');
      img.src = formArray.logo;
      img.alt = 'Form logo';
      img.style.cssText = 'height:48px;width:48px;object-fit:contain;display:block;';
      logoWrapper.appendChild(img);
      formEl.insertBefore(logoWrapper, formEl.firstChild);
      clearInterval(interval);
    }
  }, 300);
  return () => clearInterval(interval);
}, [formArray]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const encryptedId = searchParams.get('id');
        const decryptedId = await fetchDecryptedId(encryptedId);
        await checkForm(decryptedId);
      } catch (error) {
        console.error('Error fetching form data:', error);
      }
    };
    fetchData();
  }, [location.search]);

  useEffect(() => {
    if (!formArray) {
      setSurvey(null);
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const encryptedFormId = searchParams.get('id');
    const formJson = sanitizeSurveyFormJson(structuredClone(formArray));
    if (formJson.oneResponsePerEmail) {
      const emailQuestion = {
        type: "text",
        name: "__submitter_email__",
        title: "Please enter your email address",
        isRequired: true,
        inputType: "email",
        validators: [{ type: "email" }],
      };
      if (formJson.pages && formJson.pages.length > 0) {
        formJson.pages[0].elements = [
          emailQuestion,
          ...(formJson.pages[0].elements || []),
        ];
      }
    }
    const nextSurvey = new Model(ensureS3FileQuestions(formJson));
    nextSurvey.onAfterRenderQuestion?.add((survey, options) => {
      if (options.question.getType() === "file") {
        options.question.pageSize = 1000;
      }
    });
    bindSurveyFileHandlers(nextSurvey, {
      encryptedFormId,
      token: getTokenFromCookie(),
      formJson,
    });
    applyFileSourceTypes(formJson);
     if (formJson.oneResponsePerEmail) {
      let emailCheckTimeout = null;
      let emailIsBlocked = false; 

      nextSurvey.onValueChanged?.add((sender, options) => {
        if (options.name !== "__submitter_email__") return;
        const email = options.value;
        const question = sender.getQuestionByName("__submitter_email__");

        if (emailCheckTimeout) clearTimeout(emailCheckTimeout);
        if (!email || !formId) return;

        emailCheckTimeout = setTimeout(async () => {
          try {
            const checkRes = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/forms/${formId}/check-email`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
              }
            );
            const checkData = await checkRes.json();
            if (!checkData.allowed) {
              question?.addError(checkData.message || "You have already submitted this form with this email.");
              emailIsBlocked = true; 
            } else {
              question?.clearErrors();
              emailIsBlocked = false; 
            }
            sender.render(); 
          } catch (err) {
            console.error("Email check failed:", err);
          }
        }, 600);
      });
      nextSurvey.onCompleting?.add((sender, options) => {
        if (emailIsBlocked) {
          options.allowComplete = false;
          const question = sender.getQuestionByName("__submitter_email__");
          question?.addError("You have already submitted this form with this email.");
          sender.render();
        }
      });
    }
    nextSurvey.onDownloadFile?.add((_, options) => {
      const blobUrl = options.fileValue?.content || options.content;
      options.callback("success", blobUrl);
    });
    nextSurvey.onComplete?.add((s) => {
      setSurvey(null);
      alertResults(s);
    });
    setSurvey(nextSurvey);
  }, [alertResults, formArray, location.search]);

  return (
    <div>
      <ToastContainer />
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} /> {/* Modal component */}
      {survey && <Survey model={survey} />}
    </div>
  );
}


